// Atlas Phase 2 — emit one channel observation per LIVE tune-in lifecycle.
//
// The data the Event Finder (Phase 5) ranks by. Captures, per tune-in:
//   • connected:true + last-seen metrics   — reached playback
//   • connected:false                       — fatal error before playback started
//   • connected:true + droppedAfterSeconds  — fatal error AFTER playback started
//
// VOD has no stable (sourceId, itemId) live identity, so it is NOT captured —
// `startTuneIn` is only ever called for `kind === 'live'`, and every other hook
// no-ops when there's no current tune-in (so a VOD player can call the error
// hook freely). Module-level singleton state (NOT window.*, per directive #7)
// so a single pagehide beacon flushes the batch across re-mounts.

import { onMounted, onBeforeUnmount } from 'vue';
import { api, type ObservationInput } from '@/lib/api';
import { refreshObservationSignals } from '@/composables/useObservationSignals';
import { log } from '@/lib/log';

export interface ObservationMetrics {
    resHeight?: number | null;
    bitrateBps?: number | null;
    fps?: number | null;
    videoCodec?: string | null;
    audioCodec?: string | null;
    audioChannels?: number | null;
    /**
     * live-audio-track-selection Phase 4 — how many tracks the CHANNEL
     * carries. Live tune-ins previously recorded neither, so the sidebar's
     * `N×audio` / `subs` chip had no data to render from on this path.
     */
    audioTracks?: number | null;
    subtitleTracks?: number | null;
}

interface CurrentTuneIn {
    sourceId: number;
    itemId: string;
    connected: boolean;
    connectedAtMs: number | null;
    droppedAfterSeconds: number | null;
    metrics: ObservationMetrics;
}

const FLUSH_DELAY_MS = 2000;

let current: CurrentTuneIn | null = null;
let batch: ObservationInput[] = [];
let flushTimer: ReturnType<typeof setTimeout> | null = null;

function scheduleFlush(): void {
    if (flushTimer) return;
    flushTimer = setTimeout(() => {
        flushTimer = null;
        void flushBatch();
    }, FLUSH_DELAY_MS);
}

async function flushBatch(): Promise<void> {
    if (batch.length === 0) return;
    const toSend = batch.splice(0, batch.length);
    try {
        await api.observations.record(toSend);
        log.debug('observation.flushed', { count: toSend.length });
        // The flushed channels' derived signals just changed server-side —
        // refresh their cache entries so the sidebar's reliability dot /
        // last-seen quality update IN-SESSION, not on the next page load.
        void refreshObservationSignals(
            toSend.map((o) => ({ sourceId: o.sourceId, id: o.itemId })),
        );
    } catch (err) {
        log.warn('observation.flush_failed', { error: (err as Error).message, count: toSend.length });
    }
}

function pushOutcome(o: ObservationInput): void {
    batch.push(o);
    scheduleFlush();
}

/**
 * "This channel actually reached playback" listeners.
 *
 * `markConnected` is already the app's single source of truth for that fact —
 * it is what turns a tune-in into a positive observation — so anything else
 * that needs to know a stream came up subscribes here instead of inventing a
 * second, divergent notion of "working" (root CLAUDE.md rule #2). The event
 * auto-advance watchdog is the first consumer: it cancels on this signal.
 */
type ConnectedListener = (sourceId: number, itemId: string) => void;
const connectedListeners = new Set<ConnectedListener>();

export function subscribeStreamConnected(listener: ConnectedListener): () => void {
    connectedListeners.add(listener);
    return () => { connectedListeners.delete(listener); };
}

/** True when this exact tune-in reached loadedmetadata before a listener attached. */
export function isTuneInConnected(sourceId: number, itemId: string): boolean {
    return current?.sourceId === sourceId && current.itemId === itemId && current.connected;
}

function notifyConnected(sourceId: number, itemId: string): void {
    for (const listener of [...connectedListeners]) {
        // One bad listener must never break observation capture, which feeds
        // the ranking every other surface depends on.
        try { listener(sourceId, itemId); } catch { /* listener-local failure */ }
    }
}

/**
 * Flush the batch NOW instead of waiting out the debounce. For flows that
 * re-fetch server-ranked data right after recording observations (the Event
 * Finder's "check quality of these results" → re-rank), where the 2s debounce
 * would make the re-fetch miss the freshest rows. Resolves when the POST (and
 * the follow-up signals refresh) has been issued.
 */
export async function flushObservationsNow(): Promise<void> {
    if (flushTimer) {
        clearTimeout(flushTimer);
        flushTimer = null;
    }
    await flushBatch();
}

/** Emit the current tune-in's outcome (once) and clear it. */
function finalizeCurrent(): void {
    if (!current) return;
    const c = current;
    current = null;
    pushOutcome({
        sourceId: c.sourceId,
        itemId: c.itemId,
        connected: c.connected,
        droppedAfterSeconds: c.droppedAfterSeconds,
        // A failed tune-in carries no metrics (mirrors the server-side rule).
        resHeight: c.connected ? (c.metrics.resHeight ?? null) : null,
        bitrateBps: c.connected ? (c.metrics.bitrateBps ?? null) : null,
        fps: c.connected ? (c.metrics.fps ?? null) : null,
        videoCodec: c.connected ? (c.metrics.videoCodec ?? null) : null,
        audioCodec: c.connected ? (c.metrics.audioCodec ?? null) : null,
        audioChannels: c.connected ? (c.metrics.audioChannels ?? null) : null,
        audioTracks: c.connected ? (c.metrics.audioTracks ?? null) : null,
        subtitleTracks: c.connected ? (c.metrics.subtitleTracks ?? null) : null,
    });
}

/**
 * Record an AD-HOC probe result (the channel ⋮ "Stream info" action) as an
 * observation. A successful ffprobe is a real connectivity + quality
 * measurement — same evidentiary weight as a tune-in connect — and a probe
 * error is a failed connect. Goes through the normal batch → flush →
 * signals-refresh pipeline, so the channel row's dot / quality line updates
 * seconds after the probe, no playback required. Module-level export (not on
 * the composable): the channel-menu actions lib has no component context.
 */
export function recordProbeObservation(
    sourceId: number,
    itemId: string,
    info: {
        error?: string | null;
        errorKind?: string | null;
        video?: string;
        audio?: string;
        audioChannels?: number;
        audioTracks?: number;
        subtitleTracks?: number;
        height?: number;
        bitrate?: number;
        frameRate?: number | null;
    },
): void {
    if (!Number.isFinite(sourceId) || !itemId) return;
    // Provider concurrent-stream cap blocked the probe — that says NOTHING
    // about the channel (it isn't offline; the account slot is busy on
    // another device). Recording it as a failed connect would poison the
    // reliability history, so it is not recorded at all (honesty doctrine:
    // observations measure the channel, not the account state).
    if (info.errorKind === 'stream_limit') {
        log.debug('observation.probe_limit_skipped', { sourceId, itemId });
        return;
    }
    const connected = !info.error && ((info.height ?? 0) > 0 || (!!info.video && info.video !== 'unknown'));
    pushOutcome({
        sourceId,
        itemId,
        connected,
        droppedAfterSeconds: null,
        resHeight: connected ? info.height || null : null,
        bitrateBps: connected ? info.bitrate || null : null,
        fps: connected ? (info.frameRate ?? null) : null,
        videoCodec: connected && info.video && info.video !== 'unknown' ? info.video : null,
        audioCodec: connected && info.audio && info.audio !== 'unknown' ? info.audio : null,
        audioChannels: connected ? info.audioChannels || null : null,
        // Track counts: 0 is a real measurement ("none detected") — only an
        // absent field maps to null/unknown.
        audioTracks: connected && typeof info.audioTracks === 'number' ? info.audioTracks : null,
        subtitleTracks: connected && typeof info.subtitleTracks === 'number' ? info.subtitleTracks : null,
    });
}

// pagehide beacon — `navigator.sendBeacon` survives unload (the api client's
// fetch would be killed). Same-origin so the auth_token cookie rides along.
function beaconFlush(): void {
    finalizeCurrent();
    if (batch.length === 0) return;
    const toSend = batch.splice(0, batch.length);
    try {
        const blob = new Blob([JSON.stringify({ observations: toSend })], { type: 'application/json' });
        const ok = navigator.sendBeacon?.('/api/observations', blob);
        if (!ok) void api.observations.record(toSend).catch(() => {});
    } catch {
        /* best-effort — the server-side janitor and next session still capture history */
    }
}

export function useObservationCapture() {
    /** Begin a new live tune-in; finalizes any previous one first. */
    function startTuneIn(sourceId: number, itemId: string): void {
        if (!Number.isFinite(sourceId) || !itemId) return;
        finalizeCurrent();
        current = {
            sourceId,
            itemId,
            connected: false,
            connectedAtMs: null,
            droppedAfterSeconds: null,
            metrics: {},
        };
    }

    /** Mark the stream as having reached playback (loadedmetadata). */
    function markConnected(): void {
        if (!current || current.connected) return;
        current.connected = true;
        current.connectedAtMs = Date.now();
        notifyConnected(current.sourceId, current.itemId);
    }

    /** Merge the latest observed quality into the current tune-in (no-op if none). */
    function noteMetrics(m: ObservationMetrics): void {
        if (!current) return;
        const into = current.metrics;
        if (m.resHeight != null) into.resHeight = m.resHeight;
        if (m.bitrateBps != null) into.bitrateBps = m.bitrateBps;
        if (m.fps != null) into.fps = m.fps;
        if (m.videoCodec != null) into.videoCodec = m.videoCodec;
        if (m.audioCodec != null) into.audioCodec = m.audioCodec;
        if (m.audioChannels != null) into.audioChannels = m.audioChannels;
        // 0 is a real measurement ("none detected"); only absent means unknown.
        if (m.audioTracks != null) into.audioTracks = m.audioTracks;
        if (m.subtitleTracks != null) into.subtitleTracks = m.subtitleTracks;
    }

    /** A fatal playback error ended the tune-in: record drop-vs-fail and finalize. */
    function markFatalError(): void {
        if (!current) return;
        if (current.connected && current.connectedAtMs != null) {
            current.droppedAfterSeconds = Math.max(
                0,
                Math.round((Date.now() - current.connectedAtMs) / 1000),
            );
        }
        finalizeCurrent();
    }

    /** End the current tune-in cleanly (e.g. user switched away). */
    function endTuneIn(): void {
        finalizeCurrent();
    }

    /**
     * Drop the current tune-in WITHOUT recording an outcome. For failures
     * that say nothing about the channel — e.g. the provider's concurrent-
     * stream limit (HTTP 458) blocked the connect because another device is
     * watching. Recording those as failed connects would poison reliability.
     */
    function abandonTuneIn(): void {
        if (!current) return;
        log.debug('observation.tunein_abandoned', {
            sourceId: current.sourceId,
            itemId: current.itemId,
        });
        current = null;
    }

    onMounted(() => {
        window.addEventListener('pagehide', beaconFlush);
    });
    onBeforeUnmount(() => {
        window.removeEventListener('pagehide', beaconFlush);
        finalizeCurrent();
        void flushBatch();
    });

    return { startTuneIn, markConnected, noteMetrics, markFatalError, endTuneIn, abandonTuneIn };
}

// Test-only — drains module-level state between tests (Phase 2.6 retro #5).
export function _resetObservationCaptureForTests(): void {
    current = null;
    batch = [];
    connectedListeners.clear();
    if (flushTimer) {
        clearTimeout(flushTimer);
        flushTimer = null;
    }
}

// Test-only — read the pending batch.
export function _peekObservationBatchForTests(): ObservationInput[] {
    return batch.slice();
}
